import type { ReactNode } from 'react'
import { go } from '../../lib/classroom'
import { lang } from '../../lib/words'
import Icon from '../Icon'

/**
 * /privacy, /terms and /support — public, signed in or not.
 *
 * These are what a district's privacy review, Apple's App Privacy answers and
 * Play's Data safety form are checked against, so every line here must match
 * what the code actually does. If you change what is stored, change this page
 * in the same commit, and change docs/STORE_LISTING.md with it.
 *
 * The contact address is written once, here and in the listing doc.
 */
export const CONTACT = 'quentin.forgues@gmail.com'
const UPDATED = { es: '13 de octubre de 2026', en: 'October 13, 2026' }

type Page = 'privacy' | 'terms' | 'support'

export default function Legal({ page }: { page: Page }) {
  const es = lang() === 'es'
  const body = page === 'privacy' ? (es ? <PrivacyEs /> : <PrivacyEn />)
    : page === 'terms' ? (es ? <TermsEs /> : <TermsEn />)
    : (es ? <SupportEs /> : <SupportEn />)
  return (
    <article className="card legal">
      <button className="link" onClick={() => go('/')}><Icon name="back" size={16} /> Adulting 101</button>
      {body}
      <p className="muted small">{es ? 'Actualizado' : 'Updated'}: {UPDATED[es ? 'es' : 'en']}</p>
      <LegalLinks />
    </article>
  )
}

export function LegalLinks() {
  const es = lang() === 'es'
  const a = (href: string, label: string) => (
    <a href={href} onClick={(e) => { e.preventDefault(); go(href) }}>{label}</a>
  )
  return (
    <nav className="legal-links small" aria-label={es ? 'Legal' : 'Legal'}>
      {a('/guia', es ? 'Guía' : 'Guide')}
      {a('/privacy', es ? 'Privacidad' : 'Privacy')}
      {a('/terms', es ? 'Términos' : 'Terms')}
      {a('/support', es ? 'Ayuda' : 'Support')}
    </nav>
  )
}

const Mail = () => <a href={`mailto:${CONTACT}`}>{CONTACT}</a>
const H = ({ children }: { children: ReactNode }) => <h2>{children}</h2>

/* ── Privacy ─────────────────────────────────────────────────────────────── */

function PrivacyEs() {
  return (
    <>
      <h1>Política de privacidad</h1>
      <p>Adulting 101 es un curso de destrezas para la vida que se usa en el salón de clases y para estudiar por tu cuenta. Lo opera Studio42, en Puerto Rico. Esta página dice exactamente qué guardamos, para qué, y cómo se borra.</p>

      <H>Lo corto</H>
      <ul>
        <li>No hay anuncios. No vendemos ni alquilamos datos. No hay analítica de terceros ni rastreo entre apps o sitios.</li>
        <li>No pedimos email, teléfono, dirección, ubicación, fotos ni contactos.</li>
        <li>No hay mensajes entre estudiantes ni perfiles públicos.</li>
        <li>Puedes borrar tu cuenta desde la app: Cuenta, luego Borrar mi cuenta.</li>
      </ul>

      <H>Qué guardamos</H>
      <ul>
        <li><b>Cuenta:</b> el nombre que escribes (como te llama la clase), tu usuario e idioma.</li>
        <li><b>Llave de acceso (passkey):</b> solo la llave pública. Tu cara, tu huella o el PIN de tu teléfono nunca salen de tu dispositivo y nunca los vemos.</li>
        <li><b>Cuentas con PIN:</b> si un maestro te crea una cuenta con PIN, guardamos el PIN cifrado (hash), nunca el PIN en sí.</li>
        <li><b>Clase:</b> a qué clases perteneces, tus respuestas en clase, tu práctica, tus puntos (XP), insignias, lo que compras en la tienda de Pichi con puntos (no hay dinero real), las metas que tu maestro verifica y los puntos que tu maestro te da o quita, con la razón.</li>
        <li><b>Notificaciones:</b> si las activas, la dirección de entrega que da tu navegador o tu teléfono, para avisarte cuando tu clase empieza.</li>
        <li><b>Seguridad:</b> una cookie de sesión para mantenerte dentro, un registro de eventos de seguridad (entrar, crear o borrar cuenta) y, por unos minutos, tu dirección IP para limitar intentos.</li>
        <li><b>Errores:</b> si la app falla, el mensaje del error, la página y el tipo de navegador, sin tu nombre.</li>
        <li><b>Comentarios:</b> lo que escribes en el botón de comentarios, con tu nombre de usuario, para poder contestarte.</li>
      </ul>

      <H>Para qué</H>
      <p>Solo para dar el curso: que la clase funcione en vivo, que tú y tu maestro vean tu progreso, y que la app sea segura. Nada más.</p>

      <H>Quién lo ve</H>
      <ul>
        <li><b>Tu maestro</b> ve tu nombre, usuario, respuestas, práctica, puntos y metas de las clases que él o ella enseña, y puede exportarlos para su registro de notas.</li>
        <li><b>Studio42</b> lo ve solo para operar y arreglar la app.</li>
        <li><b>Proveedores que procesan datos por nosotros:</b> Cloudflare guarda y sirve la app. Las notificaciones pasan por el servicio de tu teléfono o navegador (Apple, Google o Mozilla). En la web el aviso no lleva texto; en iPhone lleva el título del aviso.</li>
      </ul>

      <H>Escuelas, FERPA y menores de 13 años</H>
      <p>Cuando una escuela o un maestro usa Adulting 101, actuamos bajo la dirección de la escuela: los datos del estudiante son de la escuela y solo se usan para la educación del estudiante. Para estudiantes menores de 13 años, la escuela da el consentimiento en nombre de los padres, como permite la ley COPPA. Un padre o madre puede pedir ver o borrar los datos de su hijo a través de la escuela o escribiéndonos.</p>

      <H>Cuánto tiempo</H>
      <p>Mientras tu cuenta exista. Si borras tu cuenta, o tu maestro borra la clase, los datos se borran en ese momento. Las copias de seguridad diarias se eliminan a los 30 días.</p>

      <H>Tus derechos</H>
      <ul>
        <li><b>Borrar:</b> en la app, Cuenta, luego Borrar mi cuenta. Si no puedes entrar, pídeselo a tu maestro o escríbenos.</li>
        <li><b>Ver o corregir:</b> escríbenos y te contestamos en 30 días o menos.</li>
      </ul>

      <H>Contacto</H>
      <p>Studio42, Puerto Rico. <Mail /></p>
    </>
  )
}

function PrivacyEn() {
  return (
    <>
      <h1>Privacy policy</h1>
      <p>Adulting 101 is a life-skills course used in the classroom and for self-study. It is run by Studio42, in Puerto Rico. This page says exactly what we keep, why, and how it is deleted.</p>

      <H>The short version</H>
      <ul>
        <li>No ads. We do not sell or rent data. No third-party analytics and no tracking across apps or websites.</li>
        <li>We do not ask for an email, phone number, address, location, photos or contacts.</li>
        <li>There are no messages between students and no public profiles.</li>
        <li>You can delete your account inside the app: Account, then Delete my account.</li>
      </ul>

      <H>What we keep</H>
      <ul>
        <li><b>Account:</b> the name you type (what the class calls you), your username and language.</li>
        <li><b>Passkey:</b> only the public key. Your face, fingerprint or phone PIN never leaves your device and we never see it.</li>
        <li><b>PIN accounts:</b> if a teacher creates an account with a PIN for you, we keep the PIN hashed, never the PIN itself.</li>
        <li><b>Class:</b> which classes you belong to, your answers in class, your practice, your points (XP), badges, what you buy in Pichi's shop with points (there is no real money), the milestones your teacher verifies, and points your teacher gives or takes back, with the reason.</li>
        <li><b>Notifications:</b> if you turn them on, the delivery address your browser or phone provides, so we can tell you when your class starts.</li>
        <li><b>Security:</b> a session cookie that keeps you signed in, a log of security events (signing in, creating or deleting an account) and, for a few minutes, your IP address to limit attempts.</li>
        <li><b>Errors:</b> if the app breaks, the error message, the page and the browser type, without your name.</li>
        <li><b>Feedback:</b> what you write in the feedback button, with your username, so we can answer you.</li>
      </ul>

      <H>Why</H>
      <p>Only to run the course: so the live class works, so you and your teacher can see your progress, and so the app stays secure. Nothing else.</p>

      <H>Who sees it</H>
      <ul>
        <li><b>Your teacher</b> sees your name, username, answers, practice, points and milestones for the classes they teach, and can export them for their gradebook.</li>
        <li><b>Studio42</b> sees it only to run and fix the app.</li>
        <li><b>Service providers who process data for us:</b> Cloudflare stores and serves the app. Notifications travel through your phone's or browser's push service (Apple, Google or Mozilla). On the web the push carries no text; on iPhone it carries the alert's title.</li>
      </ul>

      <H>Schools, FERPA and children under 13</H>
      <p>When a school or teacher uses Adulting 101, we act under the school's direction: student data belongs to the school and is used only for the student's education. For students under 13, the school provides consent on the parents' behalf, as COPPA allows. A parent can ask to review or delete their child's data through the school or by writing to us.</p>

      <H>How long</H>
      <p>For as long as your account exists. If you delete your account, or your teacher deletes the class, the data is deleted at that moment. Daily backups are removed after 30 days.</p>

      <H>Your rights</H>
      <ul>
        <li><b>Delete:</b> in the app, Account, then Delete my account. If you cannot sign in, ask your teacher or write to us.</li>
        <li><b>See or correct:</b> write to us and we answer within 30 days.</li>
      </ul>

      <H>Contact</H>
      <p>Studio42, Puerto Rico. <Mail /></p>
    </>
  )
}

/* ── Terms ───────────────────────────────────────────────────────────────── */

function TermsEs() {
  return (
    <>
      <h1>Términos de uso</h1>
      <ul>
        <li>Adulting 101 es gratis. No hay compras dentro de la app ni anuncios. Los puntos y la tienda de Pichi no tienen valor en dinero.</li>
        <li>El curso enseña conceptos generales de dinero, trabajo y vida. No es consejo financiero, legal ni de impuestos para tu caso. Para decisiones importantes, habla con un adulto de confianza o un profesional.</li>
        <li>Usa un nombre que tu clase reconozca y no escribas información privada de otras personas en los comentarios.</li>
        <li>El maestro controla su clase: puede comenzar lecciones, verificar metas, dar o quitar puntos, sacar estudiantes y borrar la clase.</li>
        <li>Podemos cerrar una cuenta que se use para molestar a otros o para atacar la app.</li>
        <li>Hacemos lo posible para que la app funcione siempre, pero se ofrece tal como está.</li>
        <li>La forma en que tratamos tus datos está en la <a href="/privacy" onClick={(e) => { e.preventDefault(); go('/privacy') }}>política de privacidad</a>.</li>
        <li>Preguntas: <Mail /></li>
      </ul>
    </>
  )
}

function TermsEn() {
  return (
    <>
      <h1>Terms of use</h1>
      <ul>
        <li>Adulting 101 is free. There are no in-app purchases and no ads. Points and Pichi's shop have no cash value.</li>
        <li>The course teaches general ideas about money, work and life. It is not financial, legal or tax advice for your situation. For big decisions, talk to a trusted adult or a professional.</li>
        <li>Use a name your class will recognise, and do not write other people's private information in feedback.</li>
        <li>The teacher runs their class: they can start lessons, verify milestones, give or take back points, remove students and delete the class.</li>
        <li>We may close an account used to bother others or to attack the app.</li>
        <li>We do our best to keep the app working, but it is provided as is.</li>
        <li>How we handle your data is in the <a href="/privacy" onClick={(e) => { e.preventDefault(); go('/privacy') }}>privacy policy</a>.</li>
        <li>Questions: <Mail /></li>
      </ul>
    </>
  )
}

/* ── Support ─────────────────────────────────────────────────────────────── */

function SupportEs() {
  return (
    <>
      <h1>Ayuda</h1>
      <p>¿Primera vez? La <a href="/guia" onClick={(e) => { e.preventDefault(); go('/guia') }}>guía paso a paso</a> te lleva de la mano, para <a href="/guia" onClick={(e) => { e.preventDefault(); go('/guia') }}>estudiantes</a> y para <a href="/guia/maestros" onClick={(e) => { e.preventDefault(); go('/guia/maestros') }}>maestros</a>.</p>
      <H>Entrar</H>
      <p>No hay contraseña. Entras con tu cara, tu huella o el PIN de tu teléfono (una llave de acceso, o passkey). Si tu maestro te dio un usuario y un PIN, toca <b>Entrar con PIN</b> en la primera pantalla.</p>
      <H>Unirte a una clase</H>
      <p>Tu maestro te da un código como K47. En la app, toca <b>Entrar a una clase</b> y escríbelo.</p>
      <H>Borrar tu cuenta</H>
      <p>En la app: <b>Cuenta</b>, luego <b>Borrar mi cuenta</b>, escribe tu usuario y confirma. Se borran tu cuenta, tus respuestas, tu práctica, tus puntos y lo que compraste en la tienda. No se puede deshacer.</p>
      <p>Si no puedes entrar, tu maestro puede sacarte de la clase (y borrar tu cuenta si te la creó con PIN), o escríbenos desde cualquier email a <Mail /> con tu usuario y el código de tu clase. Lo borramos en 30 días o menos y te confirmamos.</p>
      <H>Maestros</H>
      <p>En tu clase puedes exportar el registro de la clase (CSV), crear cuentas con PIN para estudiantes que no tienen teléfono propio, sacar a un estudiante y borrar la clase completa.</p>
      <H>Contacto</H>
      <p><Mail /></p>
    </>
  )
}

function SupportEn() {
  return (
    <>
      <h1>Support</h1>
      <p>First time? The <a href="/guia" onClick={(e) => { e.preventDefault(); go('/guia') }}>step-by-step guide</a> walks you through it, for <a href="/guia" onClick={(e) => { e.preventDefault(); go('/guia') }}>students</a> and for <a href="/guia/maestros" onClick={(e) => { e.preventDefault(); go('/guia/maestros') }}>teachers</a>.</p>
      <H>Signing in</H>
      <p>There is no password. You sign in with your face, fingerprint or phone PIN (a passkey). If your teacher gave you a username and a PIN, tap <b>Sign in with a PIN</b> on the first screen.</p>
      <H>Joining a class</H>
      <p>Your teacher gives you a code like K47. In the app, tap <b>Join a class</b> and type it.</p>
      <H>Deleting your account</H>
      <p>In the app: <b>Account</b>, then <b>Delete my account</b>, type your username and confirm. Your account, answers, practice, points and shop items are deleted. It cannot be undone.</p>
      <p>If you cannot sign in, your teacher can remove you from the class (and delete your account if they created it with a PIN), or write to us from any email at <Mail /> with your username and class code. We delete it within 30 days and confirm.</p>
      <H>Teachers</H>
      <p>In your class you can export the class record (CSV), create PIN accounts for students without their own phone, remove a student, and delete the whole class.</p>
      <H>Contact</H>
      <p><Mail /></p>
    </>
  )
}
